import React from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import StandardModal, { Actions, ColorThemeName, ModalContent } from 'components/shared/StandardModal';
import TabbedAdminListPage, { TabbedAdminListPageTab } from 'hooks/TabbedAdminListPage';
import type { AdminListPageConfig, CustomModalDef } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import { DenialList } from 'models/DenialList';
import { AICriteriaSet } from 'models/flexible/AICriteriaSet';
import { PERMISSIONS } from 'models/flexible/Profile';
import { HighCostMedicationList } from 'models/HighCostMedicationList';
import { aiCriteriaSetsQueryKeys, deleteAICriteriaSet, indexAICriteriaSets } from 'services/api/admin/aiCriteriaSets';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminDenialListsQueryKeys, indexAdminDenialLists } from 'services/api/admin/denialLists';
import { adminIndexGroups } from 'services/api/admin/groups';
import {
  adminHighCostMedicationListsQueryKeys,
  indexAdminHighCostMedicationLists,
  showAdminHighCostMedicationList,
} from 'services/api/admin/highCostMedicationLists';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import CopyIcon from 'svg/CopyIcon';
import DeactivateIcon from 'svg/DeactivateIcon';
import PencilIcon from 'svg/PencilIcon';
import PencilIconOutline from 'svg/PencilIconOutline';
import TrashIcon from 'svg/TrashIcon';

import CountLink from '../shared/CountLink';
import { buildPromptListConfig } from '../shared/promptListConfig';

import AddDenialListModal from './AddDenialListModal';
import AddHighCostMedListModal from './AddHighCostMedListModal';
import DeleteDenialListModal from './DeleteDenialListModal';
import DeleteHighCostMedListModal from './DeleteHighCostMedListModal';
import GlobalDenialListSlideout from './GlobalDenialListSlideout';
import HighCostMedListSlideout from './HighCostMedListSlideout';
import UploadDenialListButton from './UploadDenialListButton';
import UploadHighCostMedListButton from './UploadHighCostMedListButton';

// ---------------------------------------------------------------------------
// Admission Prompts tab config
// ---------------------------------------------------------------------------

const admissionPromptsConfig = buildPromptListConfig({
  targetClassname: 'Review::Admission',
  title: 'Admissions Prompts',
  columnStorageKey: 'admin.admissionPrompts',
  addButtonLabel: 'Add Admission Prompt',
  archiveObjType: 'Admission Prompt',
  promptLabel: 'Admission',
  addPath: '/admin/admissions/prompts/new',
  editPath: (id) => `/admin/admissions/prompts/${id}/edit`,
  copyPath: (id) => `/admin/admissions/prompts/${id}/edit?copy=true`,
  defaultSort: 'updatedAt desc',
  clientFilterQueryKey: 'admissionPrompts',
});

// ---------------------------------------------------------------------------
// Admission Criteria tab config
// ---------------------------------------------------------------------------

const CRITERIA_INCLUDES = ['group', 'client'] as const;

const admissionCriteriaConfig: AdminListPageConfig<AICriteriaSet> = {
  title: 'Admission Criteria',
  columnStorageKey: 'admin.admissionCriteria',
  queryKey: aiCriteriaSetsQueryKeys.index(),
  fetchFn: (params, signal) => indexAICriteriaSets(params, signal, [...CRITERIA_INCLUDES]),
  includes: CRITERIA_INCLUDES,
  fetchParams: { defaultSort: 'updatedAt desc' },
  showResultCount: true,

  addButton: {
    label: 'Add Admission Criteria',
    permission: 'adminAICriteriaCreate',
    path: '/admin/admissions/criteria/new',
  },

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'criteriaSets', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o: any) => o.name,
      getOptionValue: (o: any) => o.id,
    },
    {
      key: 'groupId',
      label: 'Group',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminGroups', 'criteriaSets', 'filter'],
        fetchFn: (params: any, signal: any) => adminIndexGroups(params, signal, ['groupType']),
        params: { sortBy: 'name asc', isProvider: true },
      },
      getOptionLabel: (o: any) => (o?.groupType?.displayName ? `${o.name} (${o.groupType.displayName})` : o?.name),
      getOptionValue: (o: any) => o.id,
    },
  ],

  columns: [
    {
      id: 'client',
      header: 'Client',
      render: (row) => <BoldText>{row.client?.name || '\u2014'}</BoldText>,
    },
    {
      id: 'group',
      header: 'Group',
      render: (row) =>
        row.group?.groupType?.displayName
          ? `${row.group.name} (${row.group.groupType.displayName})`
          : row.group?.name || '\u2014',
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      render: (row) => (row.updatedAt ? simpleDateAtTime(row.updatedAt) : '\u2014'),
    },
  ],

  rowActions: [
    {
      label: 'Edit Criteria',
      icon: <PencilIcon width={18} height={18} />,
      permission: PERMISSIONS.adminAICriteriaEdit,
      onClick: (row, { navigate }) => navigate(`/admin/admissions/criteria/${row.id}/edit`),
    },
    {
      label: 'Duplicate Criteria',
      icon: <CopyIcon width={18} height={18} />,
      permission: PERMISSIONS.adminAICriteriaCreate,
      onClick: (row, { navigate }) => navigate(`/admin/admissions/criteria/${row.id}/edit?copy=true`),
    },
    {
      label: 'Archive Criteria',
      icon: <DeactivateIcon width={18} height={18} style={{ color: colors.red100 }} />,
      danger: true,
      dividerBefore: true,
      visible: (row, ctx) => !row.archived && ctx.profile.has(PERMISSIONS.adminAICriteriaDelete),
      onClick: (row, { openModal }) => openModal('archive', row),
    },
  ],
  customModals: {
    archive: {
      render: (row, show, onClose, ctx) => (
        <StandardModal title='Archive Criteria?' colorTheme={ColorThemeName.Danger} onCancel={onClose}>
          <ModalContent>
            <BodySmall>
              <strong>Are you sure you wish to archive this criteria?</strong> Once archived, it will no longer be used
              for {row.group?.name} admissions summary.
            </BodySmall>
          </ModalContent>
          <Actions>
            <Button variant='ghost' onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant='danger'
              onClick={async () => {
                await deleteAICriteriaSet(row.id);
                onClose();
                ctx.toast('Admission criteria was successfully archived');
                ctx.refetch();
              }}>
              Archive Criteria
            </Button>
          </Actions>
        </StandardModal>
      ),
    },
  } as Record<string, CustomModalDef<AICriteriaSet>>,
};

// ---------------------------------------------------------------------------
// Global Denial Lists tab config
// ---------------------------------------------------------------------------

const DENIAL_LIST_INCLUDES = ['client'] as const;

// Archived lists are rendered grayed out (but still clickable) when "Show Archived" is on.
const mutedIfArchived = (row: DenialList): React.CSSProperties | undefined =>
  row.archived ? { color: colors.black50 } : undefined;

const globalDenialListsConfig: AdminListPageConfig<DenialList> = {
  title: 'Global Denial Lists',
  columnStorageKey: 'admin.globalDenialLists',
  queryKey: adminDenialListsQueryKeys.index(),
  fetchFn: (params, signal) => indexAdminDenialLists(params, signal),
  includes: DENIAL_LIST_INCLUDES,
  fetchParams: { defaultSort: 'updated_at desc' },
  showResultCount: true,
  headerExtra: <UploadDenialListButton />,

  // "Add New List" sits next to the Upload placeholder. It opens a client picker, creates the empty
  // list, then opens the slideout to add patients. Gated on the same permission as Upload.
  addButton: {
    label: 'Manually Add New List',
    permission: 'adminDenialListCreate',
    onClick: (ctx) => ctx.openModal('addNewList', {}),
  },

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'denialLists', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o: any) => o.name,
      getOptionValue: (o: any) => o.id,
    },
    {
      key: 'archived',
      label: 'Show Archived',
      type: 'checkbox',
      // Unchecked → hide archived (archived=false); checked → omit the filter so archived show too.
      toRequest: (value: any) => (value ? undefined : 'false'),
    },
  ],

  columns: [
    {
      id: 'client',
      header: 'Client Name',
      render: (row, ctx) => (
        <ClickableName style={mutedIfArchived(row)} onClick={() => ctx.openSlideout('items', row)}>
          {row.client?.name || '—'}
        </ClickableName>
      ),
    },
    {
      id: 'patient_count',
      header: 'Patient Count',
      render: (row, ctx) => (
        <CountLink style={mutedIfArchived(row)} onClick={() => ctx.openSlideout('items', row)}>
          {row.patientCount}
        </CountLink>
      ),
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      render: (row) => (
        <span style={mutedIfArchived(row)}>{row.updatedAt ? simpleDateAtTime(row.updatedAt) : '—'}</span>
      ),
    },
  ],

  rowActions: [
    {
      // Soft-deletes (archives) the whole list. Disabled for already-archived rows. There is
      // intentionally no "Edit List" action.
      label: 'Delete Denial List',
      icon: <TrashIcon width={18} height={18} color={colors.red100} />,
      danger: true,
      permission: PERMISSIONS.adminDenialListDelete,
      disabled: (row) => row.archived,
      onClick: (row, ctx) => ctx.openModal('deleteList', row),
    },
  ],

  customModals: {
    addNewList: {
      render: (_row, _show, onClose, ctx) => <AddDenialListModal onClose={onClose} ctx={ctx} />,
    },
    // A component (not the generic `modals`) so it can invalidate the eligible-clients dropdown via
    // useInvalidateQueries when a list is deleted.
    deleteList: {
      render: (row, _show, onClose) => <DeleteDenialListModal denialList={row} onClose={onClose} />,
    },
  },

  slideouts: {
    items: {
      render: (row, onClose) => <GlobalDenialListSlideout denialList={row} onClose={onClose} />,
    },
  },
};

// ---------------------------------------------------------------------------
// High Cost Medications tab config
// ---------------------------------------------------------------------------

const HIGH_COST_MED_INCLUDES = ['client', 'tiers'] as const;

const highCostMedsConfig: AdminListPageConfig<HighCostMedicationList> = {
  title: 'High Cost Medications',
  columnStorageKey: 'admin.highCostMeds',
  queryKey: adminHighCostMedicationListsQueryKeys.index(),
  fetchFn: (params, signal) => indexAdminHighCostMedicationLists(params, signal),
  includes: HIGH_COST_MED_INCLUDES,
  // Archived lists are never surfaced — there is no "Show Archived" toggle on this tab, so the
  // filter is fixed rather than user-controlled.
  fetchParams: { defaultSort: 'updated_at desc', extra: { archived: 'false' } },
  showResultCount: true,
  headerExtra: <UploadHighCostMedListButton />,

  // Sits next to Upload Medication List: picks a client, creates the empty list, then opens the slideout to
  // add medications. Same permission as the upload, because an upload creates a list too.
  addButton: {
    label: 'Manually Add New List',
    permission: 'adminHighCostMedicationListCreate',
    onClick: (ctx) => ctx.openModal('addNewList', {}),
  },

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'highCostMedLists', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o: any) => o.name,
      getOptionValue: (o: any) => o.id,
    },
  ],

  columns: [
    {
      id: 'client',
      header: 'Client Name',
      render: (row) => <BoldText>{row.client?.name || '—'}</BoldText>,
    },
    {
      id: 'medication_count',
      header: 'Medication Count',
      render: (row, ctx) => <CountLink onClick={() => ctx.openSlideout('items', row)}>{row.medicationCount}</CountLink>,
    },
    {
      id: 'updated_at',
      header: 'Last Updated',
      render: (row) => (row.updatedAt ? simpleDateAtTime(row.updatedAt) : '—'),
    },
  ],

  rowActions: [
    {
      label: 'Edit List',
      icon: <PencilIconOutline width={18} height={18} />,
      onClick: (row, ctx) => ctx.openSlideout('items', row),
    },
    {
      label: 'Edit Cost Tier Key',
      icon: <PencilIconOutline width={18} height={18} />,
      // Describing a tier is an edit to the list, so a View Only Olio Admin never sees this entry.
      permission: PERMISSIONS.adminHighCostMedicationListEdit,
      // Not hidden or disabled for a list whose medications carry no tiers: the form opens on its
      // empty state and explains that tiers appear once medications carry them, because the key is a
      // rollup and an empty one is not something this screen can fix.
      // Navigated rather than openSlideout'd: openSlideout targets `${routeBasePath}/${row.id}` and
      // cannot carry a sub-path. The hook reads the open list off the first path segment after the
      // base, so going straight to the tiers URL opens the panel and leaves `/tiers` for it to read.
      onClick: (row, ctx) => ctx.navigate(`/admin/admissions/high-cost-medication-lists/${row.id}/tiers`),
    },
    {
      label: 'Remove',
      icon: <TrashIcon width={18} height={18} color={colors.red100} />,
      danger: true,
      dividerBefore: true,
      // A View Only Olio Admin holds admin_*_view only, so Remove never renders for them while
      // Edit List still does.
      permission: PERMISSIONS.adminHighCostMedicationListDelete,
      onClick: (row, ctx) => ctx.openModal('deleteList', row),
    },
  ],

  customModals: {
    addNewList: {
      render: (_row, _show, onClose, ctx) => <AddHighCostMedListModal onClose={onClose} ctx={ctx} />,
    },
    deleteList: {
      render: (row, _show, onClose) => <DeleteHighCostMedListModal highCostMedicationList={row} onClose={onClose} />,
    },
  },

  slideouts: {
    items: {
      render: (row, onClose) => <HighCostMedListSlideout highCostMedicationList={row} onClose={onClose} />,
      // A client's med list is worth linking to — pasting the URL into a ticket or a message opens
      // the panel on the right list, and a refresh keeps it open. The id rides the path, so
      // App.tsx registers `high-cost-medication-lists/:listId` and its sub-paths alongside the tab's
      // own route.
      routeBasePath: '/admin/admissions/high-cost-medication-lists',
      fetchRow: showAdminHighCostMedicationList,
    },
  },
};

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

const tabs: TabbedAdminListPageTab[] = [
  {
    label: 'Admission Prompts',
    path: '/admin/admissions/prompts',
    permission: 'adminAIPromptView',
    config: admissionPromptsConfig,
  },
  {
    label: 'Admission Criteria',
    path: '/admin/admissions/criteria',
    permission: 'adminAICriteriaView',
    config: admissionCriteriaConfig,
  },
  {
    label: 'Global Denial Lists',
    path: '/admin/admissions/denial-lists',
    permission: 'adminDenialListView',
    config: globalDenialListsConfig,
  },
  {
    label: 'High Cost Medications',
    path: '/admin/admissions/high-cost-medication-lists',
    permission: 'adminHighCostMedicationListView',
    config: highCostMedsConfig,
  },
];

export default function Admissions() {
  return <TabbedAdminListPage pageTitle='Admissions' tabs={tabs} />;
}

const BoldText = styled.span`
  font-weight: 500;
`;

// Same look as BoldText — only adds a pointer cursor since the name opens the slideout.
const ClickableName = styled(BoldText)`
  cursor: pointer;
`;
